import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { clubSidebar } from '@/config/sidebarConfig.ts';
import { getTeamApi, getTeamsApi } from '@/lib/api/teamsApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { PAY_STATUS_CONFIG, REG_STATUS_CONFIG } from '@/lib/status-config';
import { getTeamSportingCaptain, getTeamSportingCaptainEmail } from '@/lib/team-sporting-captain';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { CompetitionStatus, PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  MapPin,
  Phone,
  Search,
  Trophy,
  Users,
  Users2,
  X,
  XCircle,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Dialog from '../../components/ui/Dialog';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Constants ──────────────────────────────────────────────────
const PAGE_SIZE = 20;

// ─── Badge config ────────────────────────────────────────────────
const COMP_STATUS_LABEL: Partial<Record<CompetitionStatus, string>> = {
  [CompetitionStatus.REGISTRATION_OPEN]: 'Inscriptions ouvertes',
  [CompetitionStatus.REGISTRATION_CLOSED]: 'Inscriptions fermées',
  [CompetitionStatus.IN_PROGRESS]: 'En cours',
  [CompetitionStatus.FINISHED]: 'Terminé',
  [CompetitionStatus.CANCELLED]: 'Annulé',
};

// ─── Helpers ────────────────────────────────────────────────────
function initials(firstName?: string | null, lastName?: string | null): string {
  const f = (firstName ?? '').charAt(0).toUpperCase();
  const l = (lastName ?? '').charAt(0).toUpperCase();
  return `${f}${l}` || '?';
}

function formatMoneyCents(amount?: number | null) {
  if (amount == null || amount <= 0) return null;
  return `${(amount / 100).toFixed(2)} €`;
}

// ─── Team Detail Modal (lecture seule) ───────────────────────────
interface TeamDetailModalProps {
  teamId: number | null;
  onClose: () => void;
}

function TeamDetailModal({ teamId, onClose }: TeamDetailModalProps) {
  const { data: team, isLoading } = useQuery({
    queryKey: queryKeys.teams.detail(teamId!),
    queryFn: () => getTeamApi(teamId!),
    enabled: teamId !== null,
  });

  const regCfg = team ? REG_STATUS_CONFIG[team.registrationStatus] : null;
  const payCfg = team ? PAY_STATUS_CONFIG[team.paymentStatus] : null;

  const compStatusLabel = team?.competition
    ? (COMP_STATUS_LABEL[team.competition.status as CompetitionStatus] ?? team.competition.status)
    : null;

  return (
    <Dialog
      open={teamId !== null}
      onClose={onClose}
      title={team?.name ?? 'Détail équipe'}
      // largeur mobile + hauteur max + overflow pour activer le scroll interne
      className='max-w-xl w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-hidden'
    >
      {/* Layout modal : header fixe + body scroll */}
      <div className='flex flex-col max-h-[85vh]'>
        {/* Header */}
        <div className='shrink-0 sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-start justify-between rounded-t-2xl z-10'>
          <div className='min-w-0 pr-4'>
            {isLoading ? (
              <div className='h-5 w-48 animate-pulse rounded bg-slate-200' />
            ) : (
              <>
                <h2 className='text-lg font-bold text-slate-900 truncate'>{team?.name}</h2>
                {team?.competition && <p className='text-sm text-slate-500 mt-0.5 truncate'>{team.competition.name}</p>}
              </>
            )}
          </div>

          <button
            onClick={onClose}
            className='p-2 rounded-lg hover:bg-slate-100 transition-colors shrink-0'
            aria-label='Fermer'
          >
            <X className='w-5 h-5 text-slate-500' />
          </button>
        </div>

        {/* Body scrollable */}
        <div className='flex-1 overflow-y-auto'>
          {/* Loading skeleton */}
          {isLoading && (
            <div className='p-4 sm:p-6 space-y-4'>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className='h-14 animate-pulse rounded-xl bg-slate-100' />
              ))}
            </div>
          )}

          {/* Content */}
          {!isLoading && team && (
            <div className='p-4 sm:p-6 space-y-5'>
              {/* Competition */}
              {team.competition && (
                <div className='bg-blue-50 rounded-xl p-4'>
                  <p className='text-xs font-semibold text-blue-500 uppercase tracking-wide mb-2'>Concours</p>
                  <p className='font-semibold text-slate-900'>{team.competition.name}</p>

                  <div className='flex flex-wrap gap-3 mt-2 text-sm text-slate-500'>
                    <span className='flex items-center gap-1'>
                      <Calendar className='w-3.5 h-3.5 shrink-0' />
                      {formatDateAbbreviatedFr(team.competition.date)}
                    </span>
                    <span className='flex items-center gap-1'>
                      <MapPin className='w-3.5 h-3.5 shrink-0' />
                      {team.competition.venue ?? team.competition.city ?? '—'}
                    </span>
                  </div>

                  {compStatusLabel && (
                    <span className='inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700'>
                      {compStatusLabel}
                    </span>
                  )}
                </div>
              )}

              {/* Status badges */}
              <div className='flex flex-wrap gap-2'>
                {regCfg && (
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${regCfg.bg} ${regCfg.text}`}
                  >
                    {team.registrationStatus === TeamRegistrationStatus.CONFIRMED ? (
                      <CheckCircle className='w-3.5 h-3.5' />
                    ) : team.registrationStatus === TeamRegistrationStatus.PENDING ? (
                      <Clock className='w-3.5 h-3.5' />
                    ) : (
                      <XCircle className='w-3.5 h-3.5' />
                    )}
                    {regCfg.label}
                  </span>
                )}

                {payCfg && (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${payCfg.bg} ${payCfg.text}`}
                  >
                    Paiement : {payCfg.label}
                  </span>
                )}

                {team.paidAmount != null && team.paidAmount > 0 && (
                  <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700'>
                    {formatMoneyCents(team.paidAmount)}
                  </span>
                )}
              </div>

              {/* Captain */}
              {(getTeamSportingCaptain(team) || team.captainPhone) && (
                <div className='bg-slate-50 rounded-xl p-4'>
                  <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3'>Capitaine</p>
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0'>
                      <span className='text-sm font-bold text-indigo-700'>
                        {initials(getTeamSportingCaptain(team)?.firstName, getTeamSportingCaptain(team)?.lastName)}
                      </span>
                    </div>

                    <div className='min-w-0'>
                      {getTeamSportingCaptain(team) && (
                        <p className='font-semibold text-slate-900 truncate'>
                          {getTeamSportingCaptain(team)?.firstName} {getTeamSportingCaptain(team)?.lastName}
                        </p>
                      )}
                      {getTeamSportingCaptainEmail(team) && (
                        <p className='text-sm text-slate-500 truncate'>{getTeamSportingCaptainEmail(team)}</p>
                      )}
                      {team.captainPhone && (
                        <p className='text-sm text-slate-500 flex items-center gap-1 mt-0.5'>
                          <Phone className='w-3.5 h-3.5 shrink-0' />
                          {team.captainPhone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Players */}
              {team.players && team.players.length > 0 && (
                <div>
                  <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3'>
                    Joueurs ({team.players.length})
                  </p>

                  <div className='space-y-2'>
                    {[...team.players]
                      .sort((a, b) => a.playerOrder - b.playerOrder)
                      .map(player => (
                        <div key={player.id} className='flex items-center gap-3 p-3 rounded-xl bg-slate-50'>
                          <div className='w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0'>
                            <span className='text-xs font-bold text-slate-600'>{player.playerOrder}</span>
                          </div>

                          <div className='flex-1 min-w-0'>
                            <p className='font-medium text-slate-900 truncate'>
                              {player.firstName} {player.lastName}
                            </p>
                            {player.licenseNumber && (
                              <p className='text-xs text-slate-400'>Licence : {player.licenseNumber}</p>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {team.notes && (
                <div className='bg-amber-50 rounded-xl p-4'>
                  <p className='text-xs font-semibold text-amber-700 mb-1'>Notes</p>
                  <p className='text-sm text-amber-900'>{team.notes}</p>
                </div>
              )}

              {team.paidAt && <p className='text-xs text-slate-400'>Payé le {formatDateAbbreviatedFr(team.paidAt)}</p>}
              <p className='text-xs text-slate-400'>Inscrit le {formatDateAbbreviatedFr(team.createdAt)}</p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────
export default function ClubCompetitions() {
  const clubId = useAuthStore(s => s.user)?.clubId;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  // ─── URL params ───
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const regStatus = (searchParams.get('regStatus') ?? '') as TeamRegistrationStatus | '';
  const payStatus = (searchParams.get('payStatus') ?? '') as PaymentStatus | '';
  const debouncedSearch = searchParams.get('search') ?? '';

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  // ─── Local search state ───
  const [searchInput, setSearchInput] = useState(debouncedSearch);

  // ─── Modal ───
  const [openTeamId, setOpenTeamId] = useState<number | null>(null);

  // ─── Teams query ───
  const teamsParams = {
    clubId: clubId ?? undefined,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(regStatus && { registrationStatus: regStatus }),
    ...(payStatus && { paymentStatus: payStatus }),
    page,
    limit: PAGE_SIZE,
  };

  const {
    data: teamsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.teams.list(teamsParams),
    queryFn: () => getTeamsApi(teamsParams),
    enabled: !!clubId && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const teams = teamsData?.data ?? [];
  const totalTeams = teamsData?.meta?.total ?? 0;
  const totalPages = teamsData?.meta?.totalPages ?? 1;
  const from = totalTeams === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalTeams);

  // ─── Stats dérivées des équipes chargées ───
  const confirmedCount = teams.filter(t => t.registrationStatus === TeamRegistrationStatus.CONFIRMED).length;
  const pendingCount = teams.filter(t => t.registrationStatus === TeamRegistrationStatus.PENDING).length;
  const competitionIds = new Set(teams.map(t => t.competitionId));

  // ─── Debounce search → URL ───
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        <PageHeader
          title='Inscriptions de mes membres'
          subtitle='Consultez toutes les équipes inscrites dans des concours'
        />

        {/* ─── Stats ── */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8'>
          <StatCard
            icon={Users2}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Équipes inscrites'
            value={String(totalTeams)}
            subtitle='Toutes compétitions'
          />
          <StatCard
            icon={CheckCircle}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Confirmées'
            value={String(confirmedCount)}
            subtitle='Sur la page courante'
          />
          <StatCard
            icon={Clock}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='En attente'
            value={String(pendingCount)}
            subtitle='Réponse organisateur'
          />
          <StatCard
            icon={Trophy}
            iconBg='bg-indigo-100'
            iconColor='text-indigo-600'
            label='Concours différents'
            value={String(competitionIds.size)}
            subtitle='Sur la page courante'
          />
        </div>

        {/* ─── Filters ── */}
        <div className='flex flex-col lg:flex-row lg:items-center gap-3 mb-6'>
          <div className='relative w-full lg:flex-1 lg:min-w-[240px] lg:max-w-sm'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder='Rechercher une équipe…'
              className='w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm'
            />
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 w-full lg:w-auto'>
            <select
              value={regStatus}
              onChange={e => {
                setSearchParams(
                  prev => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value) next.set('regStatus', e.target.value);
                    else next.delete('regStatus');
                    next.set('page', '1');
                    return next;
                  },
                  { replace: true },
                );
              }}
              className='w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm text-slate-700 bg-white'
            >
              <option value=''>Tous les statuts</option>
              {Object.entries(REG_STATUS_CONFIG).map(([value, cfg]) => (
                <option key={value} value={value}>
                  {cfg.label}
                </option>
              ))}
            </select>

            <select
              value={payStatus}
              onChange={e => {
                setSearchParams(
                  prev => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value) next.set('payStatus', e.target.value);
                    else next.delete('payStatus');
                    next.set('page', '1');
                    return next;
                  },
                  { replace: true },
                );
              }}
              className='w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm text-slate-700 bg-white'
            >
              <option value=''>Tous les paiements</option>
              {Object.entries(PAY_STATUS_CONFIG).map(([value, cfg]) => (
                <option key={value} value={value}>
                  {cfg.label}
                </option>
              ))}
            </select>
          </div>

          {isFetching && !isLoading && <span className='text-xs text-slate-400 lg:ml-auto'>Actualisation…</span>}
        </div>

        {/* ─── Table/Card list ── */}
        <div className='bg-white rounded-2xl border border-slate-200 overflow-hidden'>
          {/* Desktop header */}
          <div className='hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
            <div className='col-span-3'>Concours</div>
            <div className='col-span-2'>Équipe</div>
            <div className='col-span-2'>Inscription</div>
            <div className='col-span-2'>Paiement</div>
            <div className='col-span-2'>Capitaine</div>
            <div className='col-span-1 text-right'>Actions</div>
          </div>

          {/* Body */}
          {!clubId ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <Users2 className='w-12 h-12 opacity-30' />
              <p className='text-sm font-medium'>Aucun club associé à votre compte</p>
            </div>
          ) : isLoading ? (
            <div>
              {/* Mobile skeleton */}
              <div className='md:hidden p-4 space-y-3'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className='h-24 animate-pulse rounded-2xl bg-slate-100' />
                ))}
              </div>

              {/* Desktop skeleton */}
              <div className='hidden md:block'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50'>
                    <div className='col-span-3 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
                  </div>
                ))}
              </div>
            </div>
          ) : teams.length === 0 ? (
            <div className='py-16 text-center text-sm text-slate-400'>
              Aucune inscription trouvée {(debouncedSearch || regStatus || payStatus) && ' avec ces filtres'}
            </div>
          ) : (
            <>
              {/* MOBILE: cards */}
              <div className='md:hidden p-4 space-y-3'>
                {teams.map(team => {
                  const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
                  const payCfg = PAY_STATUS_CONFIG[team.paymentStatus];
                  const playerCount = team.players?.length ?? 0;
                  const money = formatMoneyCents(team.paidAmount);
                  const venue = team.competition?.venue ?? team.competition?.city ?? null;

                  return (
                    <div key={team.id} className='rounded-2xl border border-slate-200 bg-white p-4'>
                      <div className='flex items-start justify-between gap-3'>
                        <div className='min-w-0'>
                          <p className='text-sm font-bold text-slate-900 truncate'>{team.name}</p>
                          <div className='flex items-center gap-2 text-xs text-slate-500 mt-1'>
                            <span className='inline-flex items-center gap-1'>
                              <Users className='w-3.5 h-3.5' />
                              {playerCount > 0 ? `${playerCount} joueur${playerCount > 1 ? 's' : ''}` : '—'}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() => setOpenTeamId(team.id)}
                          className='p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50'
                          aria-label='Voir le détail'
                          title='Voir le détail'
                        >
                          <Eye className='w-4 h-4' />
                        </button>
                      </div>

                      {team.competition && (
                        <div className='mt-3 rounded-xl bg-slate-50 p-3'>
                          <p className='text-xs font-semibold text-slate-700 truncate'>{team.competition.name}</p>
                          <div className='mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500'>
                            <span className='inline-flex items-center gap-1'>
                              <Calendar className='w-3.5 h-3.5' />
                              {formatDateAbbreviatedFr(team.competition.date)}
                            </span>

                            {venue && (
                              <span className='inline-flex items-center gap-1 min-w-0'>
                                <MapPin className='w-3.5 h-3.5 shrink-0' />
                                <span className='truncate'>{venue}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className='mt-3 flex flex-wrap gap-2'>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${regCfg.bg} ${regCfg.text}`}
                        >
                          {regCfg.label}
                        </span>

                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${payCfg.bg} ${payCfg.text}`}
                        >
                          Paiement : {payCfg.label}
                        </span>

                        {money && (
                          <span className='inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700'>
                            {money}
                          </span>
                        )}
                      </div>

                      <div className='mt-3'>
                        <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Capitaine</p>

                        {getTeamSportingCaptain(team) ? (
                          <div className='mt-2 flex items-center gap-2 min-w-0'>
                            <div className='w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0'>
                              <span className='text-xs font-bold text-indigo-700'>
                                {initials(
                                  getTeamSportingCaptain(team)?.firstName,
                                  getTeamSportingCaptain(team)?.lastName,
                                )}
                              </span>
                            </div>

                            <div className='min-w-0'>
                              <p className='text-xs font-medium text-slate-700 truncate'>
                                {getTeamSportingCaptain(team)?.firstName} {getTeamSportingCaptain(team)?.lastName}
                              </p>
                              {team.captainPhone && (
                                <p className='text-xs text-slate-500 flex items-center gap-1 truncate'>
                                  <Phone className='w-3.5 h-3.5 shrink-0' />
                                  {team.captainPhone}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className='mt-1 text-xs text-slate-400'>—</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* DESKTOP: grid rows */}
              <div className='hidden md:block'>
                {teams.map(team => {
                  const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
                  const payCfg = PAY_STATUS_CONFIG[team.paymentStatus];
                  const playerCount = team.players?.length ?? 0;

                  return (
                    <div
                      key={team.id}
                      className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50 items-center hover:bg-slate-50 transition'
                    >
                      {/* Concours */}
                      <div className='col-span-3 min-w-0'>
                        {team.competition ? (
                          <>
                            <p className='font-semibold text-slate-900 text-sm truncate'>{team.competition.name}</p>
                            <p className='text-xs text-slate-400 flex items-center gap-1 mt-0.5'>
                              <Calendar className='w-3 h-3 shrink-0' />
                              {formatDateAbbreviatedFr(team.competition.date)}
                            </p>
                            {(team.competition.venue ?? team.competition.city) && (
                              <p className='text-xs text-slate-400 flex items-center gap-1'>
                                <MapPin className='w-3 h-3 shrink-0' />
                                <span className='truncate'>{team.competition.venue ?? team.competition.city}</span>
                              </p>
                            )}
                          </>
                        ) : (
                          <span className='text-xs text-slate-300'>—</span>
                        )}
                      </div>

                      {/* Équipe */}
                      <div className='col-span-2 min-w-0'>
                        <p className='font-semibold text-slate-900 text-sm truncate'>{team.name}</p>
                        <div className='flex items-center gap-1 mt-0.5 text-xs text-slate-400'>
                          <Users className='w-3 h-3 shrink-0' />
                          {playerCount > 0 ? `${playerCount} joueur${playerCount > 1 ? 's' : ''}` : '—'}
                        </div>
                      </div>

                      {/* Statut inscription */}
                      <div className='col-span-2'>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${regCfg.bg} ${regCfg.text}`}
                        >
                          {regCfg.label}
                        </span>
                      </div>

                      {/* Paiement */}
                      <div className='col-span-2'>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${payCfg.bg} ${payCfg.text}`}
                        >
                          {payCfg.label}
                        </span>
                        {team.paidAmount != null && team.paidAmount > 0 && (
                          <p className='text-xs text-slate-400 mt-0.5'>{formatMoneyCents(team.paidAmount)}</p>
                        )}
                      </div>

                      {/* Capitaine */}
                      <div className='col-span-2 min-w-0'>
                        {getTeamSportingCaptain(team) ? (
                          <div className='flex items-center gap-2'>
                            <div className='w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0'>
                              <span className='text-xs font-bold text-indigo-700'>
                                {initials(
                                  getTeamSportingCaptain(team)?.firstName,
                                  getTeamSportingCaptain(team)?.lastName,
                                )}
                              </span>
                            </div>
                            <div className='min-w-0'>
                              <p className='text-xs font-medium text-slate-700 truncate'>
                                {getTeamSportingCaptain(team)?.firstName} {getTeamSportingCaptain(team)?.lastName}
                              </p>
                              {team.captainPhone && (
                                <p className='text-xs text-slate-400 flex items-center gap-0.5 truncate'>
                                  <Phone className='w-3 h-3 shrink-0' />
                                  {team.captainPhone}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className='text-xs text-slate-300'>—</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className='col-span-1 flex items-center justify-end'>
                        <button
                          onClick={() => setOpenTeamId(team.id)}
                          title='Voir le détail'
                          aria-label='Voir le détail'
                          className='p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition'
                        >
                          <Eye className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalTeams > 0 && (
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-200 px-4 sm:px-6 py-4'>
              <p className='text-sm text-slate-500'>
                Affichage de <span className='font-semibold text-slate-700'>{from}</span> à{' '}
                <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
                <span className='font-semibold text-slate-700'>{totalTeams.toLocaleString('fr-FR')}</span> équipes
              </p>

              {totalPages > 1 && (
                <div className='flex items-center gap-1 flex-wrap justify-start sm:justify-end'>
                  <button
                    onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                    disabled={page === 1}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                    aria-label='Page précédente'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>

                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setParam('page', String(p))}
                        aria-current={p === page ? 'page' : undefined}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
                    disabled={page >= totalPages}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                    aria-label='Page suivante'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Team Detail Modal */}
      <TeamDetailModal teamId={openTeamId} onClose={() => setOpenTeamId(null)} />
    </DashboardLayout>
  );
}
